/**
 * Validador de identificação fiscal (taxId) do tenant por país. Espelho TS de
 * `apps/android/.../core/taxid/TaxIdValidator.kt`.
 *
 * Validações com **DV** (dígito verificador) seguem os algoritmos oficiais:
 *  - **BR CNPJ**: módulo 11 (peso 5..2 + 6..2 dois turnos)
 *  - **AR CUIT**: módulo 11 (pesos [5,4,3,2,7,6,5,4,3,2])
 *  - **CL RUT**: módulo 11 (multiplicadores 2..7 cíclicos a partir do dígito da direita)
 *  - **PY RUC**: módulo 11 (peso 2..N)
 *  - **MX RFC PM**: regex (12 caracteres alfanum.)
 *  - **US EIN**: 9 dígitos com prefixo do IRS
 *  - **IT P.IVA**: módulo 10 (Luhn-like)
 */
import type { Country } from './enums';

export type TaxIdError =
  | { kind: 'Required'; message: string }
  | { kind: 'WrongLength'; expectedMin: number; expectedMax: number; message: string }
  | { kind: 'InvalidChars'; message: string }
  | { kind: 'InvalidCheckDigit'; message: string }
  | { kind: 'InvalidFormat'; message: string };

export type TaxIdAcceptedChars = 'DIGITS' | 'ALPHANUM';

export type TaxIdSpec = {
  /** Sigla fiscal do país (CNPJ/CUIT/RUT/RUC/RFC/EIN/P.IVA). Não traduzida —
   *  são nomes próprios universais. Usada como rótulo na etiqueta. */
  label: string;
  placeholder: string;
  acceptedChars: TaxIdAcceptedChars;
  maxLen: number;
  format: (raw: string) => string;
  validate: (raw: string) => TaxIdError | null;
};

// -------------------- BR --------------------

const BR_CNPJ_LEN = 14;

function formatBrCnpj(d: string): string {
  let out = d.slice(0, 2);
  if (d.length > 2) out += '.' + d.slice(2, 5);
  if (d.length > 5) out += '.' + d.slice(5, 8);
  if (d.length > 8) out += '/' + d.slice(8, 12);
  if (d.length > 12) out += '-' + d.slice(12, 14);
  return out;
}

function mod11Dv(body: string, weights: readonly number[]): number {
  if (body.length !== weights.length) {
    throw new Error('mod11Dv: body length must match weights');
  }
  let sum = 0;
  for (let i = 0; i < body.length; i++) {
    sum += Number.parseInt(body[i] ?? '0', 10) * (weights[i] ?? 0);
  }
  const rest = sum % 11;
  return rest < 2 ? 0 : 11 - rest;
}

function validateBrCnpj(input: string): TaxIdError | null {
  const d = input.replace(/\D/g, '');
  if (d.length === 0) return { kind: 'Required', message: 'CNPJ obrigatório' };
  if (d.length !== BR_CNPJ_LEN) {
    return {
      kind: 'WrongLength',
      expectedMin: BR_CNPJ_LEN,
      expectedMax: BR_CNPJ_LEN,
      message: 'CNPJ deve ter 14 dígitos',
    };
  }
  if (d.split('').every((c) => c === d[0])) {
    return { kind: 'InvalidCheckDigit', message: 'CNPJ inválido' };
  }
  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const dv1 = mod11Dv(d.substring(0, 12), w1);
  const dv2 = mod11Dv(d.substring(0, 13), w2);
  if (Number.parseInt(d[12] ?? '', 10) === dv1 && Number.parseInt(d[13] ?? '', 10) === dv2) {
    return null;
  }
  return { kind: 'InvalidCheckDigit', message: 'CNPJ com dígito verificador inválido' };
}

// -------------------- MX --------------------

const MX_RFC_PM_LEN = 12;
const MX_RFC_PM_REGEX = /^[A-Z&Ñ]{3}\d{6}[A-Z0-9]{3}$/;

function validateMxRfcPm(input: string): TaxIdError | null {
  const u = input
    .toUpperCase()
    .split('')
    .filter((c) => /[A-Z0-9&Ñ]/.test(c))
    .join('');
  if (u.length === 0) return { kind: 'Required', message: 'RFC obrigatório' };
  if (u.length !== MX_RFC_PM_LEN) {
    return {
      kind: 'WrongLength',
      expectedMin: MX_RFC_PM_LEN,
      expectedMax: MX_RFC_PM_LEN,
      message: 'RFC pessoa moral deve ter 12 caracteres',
    };
  }
  if (!MX_RFC_PM_REGEX.test(u)) {
    return { kind: 'InvalidFormat', message: 'RFC pessoa moral inválido (esperado AAA######AAA)' };
  }
  return null;
}

// -------------------- US --------------------

const US_EIN_LEN = 9;
const US_EIN_VALID_PREFIXES = new Set([
  1, 2, 3, 4, 5, 6,
  10, 11, 12, 13, 14, 15, 16,
  20, 21, 22, 23, 24, 25, 26, 27,
  30, 31, 32, 33, 34, 35, 36, 37, 38, 39,
  40, 41, 42, 43, 44, 45, 46, 47, 48,
  50, 51, 52, 53, 54, 55, 56, 57, 58, 59,
  60, 61, 62, 63, 64, 65, 66, 67, 68,
  71, 72, 73, 74, 75, 76, 77,
  80, 81, 82, 83, 84, 85, 86, 87, 88,
  90, 91, 92, 93, 94, 95, 98, 99,
]);

function formatUsEin(d: string): string {
  let out = d.slice(0, 2);
  if (d.length > 2) out += '-' + d.slice(2, 9);
  return out;
}

function validateUsEin(input: string): TaxIdError | null {
  const d = input.replace(/\D/g, '');
  if (d.length === 0) return { kind: 'Required', message: 'EIN obrigatório' };
  if (d.length !== US_EIN_LEN) {
    return {
      kind: 'WrongLength',
      expectedMin: US_EIN_LEN,
      expectedMax: US_EIN_LEN,
      message: 'EIN deve ter 9 dígitos',
    };
  }
  const prefix = Number.parseInt(d.substring(0, 2), 10);
  if (!US_EIN_VALID_PREFIXES.has(prefix)) {
    return { kind: 'InvalidFormat', message: 'Prefixo do EIN não é emitido pelo IRS' };
  }
  return null;
}

// -------------------- CA --------------------

const CA_BN_LEN = 9;

function formatCaBn(d: string): string {
  let out = d.slice(0, 5);
  if (d.length > 5) out += '-' + d.slice(5, 9);
  return out;
}

function validateCaBn(input: string): TaxIdError | null {
  // Business Number da CRA: 9 dígitos. Não há checksum oficial público
  // (o DV varia por programa RT/RP/RC), então validamos por comprimento —
  // mesmo tratamento length-based do US EIN.
  const d = input.replace(/\D/g, '');
  if (d.length === 0) return { kind: 'Required', message: 'Business Number obrigatório' };
  if (d.length !== CA_BN_LEN) {
    return {
      kind: 'WrongLength',
      expectedMin: CA_BN_LEN,
      expectedMax: CA_BN_LEN,
      message: 'Business Number deve ter 9 dígitos',
    };
  }
  return null;
}

// -------------------- IT --------------------

const IT_PIVA_LEN = 11;

function validateItPiva(input: string): TaxIdError | null {
  const d = input.replace(/\D/g, '');
  if (d.length === 0) return { kind: 'Required', message: 'P.IVA obrigatório' };
  if (d.length !== IT_PIVA_LEN) {
    return {
      kind: 'WrongLength',
      expectedMin: IT_PIVA_LEN,
      expectedMax: IT_PIVA_LEN,
      message: 'P.IVA deve ter 11 dígitos',
    };
  }
  // Algoritmo Agenzia delle Entrate: posições pares (0,2,4,...) somam direto;
  // ímpares (1,3,5,...) dobram e, se >9, subtrai 9. Soma %10 deve ser 0.
  let sum = 0;
  for (let i = 0; i < IT_PIVA_LEN; i++) {
    const digit = Number.parseInt(d[i] ?? '0', 10);
    if (i % 2 === 0) {
      sum += digit;
    } else {
      const doubled = digit * 2;
      sum += doubled > 9 ? doubled - 9 : doubled;
    }
  }
  if (sum % 10 !== 0) {
    return { kind: 'InvalidCheckDigit', message: 'P.IVA com dígito verificador inválido' };
  }
  return null;
}

function formatItPiva(d: string): string {
  let out = d.slice(0, 4);
  if (d.length > 4) out += ' ' + d.slice(4, 8);
  if (d.length > 8) out += ' ' + d.slice(8, 11);
  return out;
}

// -------------------- public API --------------------

const SPECS: Readonly<Record<Country, TaxIdSpec>> = {
  BR: {
    label: 'CNPJ',
    placeholder: '00.000.000/0000-00',
    acceptedChars: 'DIGITS',
    maxLen: BR_CNPJ_LEN,
    format: formatBrCnpj,
    validate: validateBrCnpj,
  },
  MX: {
    label: 'RFC',
    placeholder: 'ABC010203XYZ',
    acceptedChars: 'ALPHANUM',
    maxLen: MX_RFC_PM_LEN,
    format: (raw) => raw.toUpperCase(),
    validate: validateMxRfcPm,
  },
  US: {
    label: 'EIN',
    placeholder: '12-3456789',
    acceptedChars: 'DIGITS',
    maxLen: US_EIN_LEN,
    format: formatUsEin,
    validate: validateUsEin,
  },
  CA: {
    label: 'BN',
    placeholder: '12345-6789',
    acceptedChars: 'DIGITS',
    maxLen: CA_BN_LEN,
    format: formatCaBn,
    validate: validateCaBn,
  },
  IT: {
    label: 'P.IVA',
    placeholder: '1234 5678 901',
    acceptedChars: 'DIGITS',
    maxLen: IT_PIVA_LEN,
    format: formatItPiva,
    validate: validateItPiva,
  },
};

/**
 * Monta "CNPJ 12.345.678/0001-90" (sigla do país + valor formatado), usado no
 * campo Estabelecimento da etiqueta. País ausente → devolve só o valor cru
 * (tenants legados sem `countryCode`). Valor vazio → string vazia.
 * Espelhado em Kotlin (`TaxIdValidator.labeled`).
 */
export function formatTaxIdWithLabel(country: string | undefined, raw: string): string {
  const clean = raw.trim();
  if (clean === '') return '';
  if (country == null) return clean;
  // #942 (2026-10-12) — o país do tenant é qualquer ISO alpha-2; fora dos 5
  // mercados não há máscara nem rótulo: mostra o valor cru.
  const spec = (SPECS as Partial<Record<string, TaxIdSpec>>)[country];
  if (spec === undefined) return clean;
  return `${spec.label} ${spec.format(clean)}`;
}

export const TaxIdValidator = {
  specFor(country: Country): TaxIdSpec {
    return SPECS[country];
  },
  /** Filtra a entrada do usuário aceitando apenas chars válidos e capa em maxLen. */
  cleanInput(country: Country, raw: string): string {
    const spec = SPECS[country];
    const filtered =
      spec.acceptedChars === 'DIGITS'
        ? raw.replace(/\D/g, '')
        : raw
            .split('')
            .filter((c) => /[A-Za-z0-9]/.test(c))
            .join('')
            .toUpperCase();
    return filtered.slice(0, spec.maxLen);
  },
};
